import React from 'react';
import { S, readableOn } from '../styles';
import { getTheme } from '../theme';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' });
}
function fmtDateTime(d) {
  if (!d) return '—';
  const dt = new Date(d);
  return `${dt.toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' })} · ${dt.toLocaleTimeString('en-US', { hour:'numeric', minute:'2-digit' })}`;
}
function fmtPhone(p) {
  if (!p) return null;
  const digits = String(p).replace(/\D/g, '').replace(/^1/, '');
  if (digits.length !== 10) return p;
  return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
}

function hexToRgba(hex, alpha) {
  const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  if (!r) return `rgba(128,128,128,${alpha})`;
  return `rgba(${parseInt(r[1],16)},${parseInt(r[2],16)},${parseInt(r[3],16)},${alpha})`;
}

function LsField({ label, value, isDark, mono = false, empty = false, tint = null }) {
  return (
    <div>
      <div style={{ fontSize:10, color: isDark ? '#5a6a8a' : '#999', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4, fontWeight:600 }}>{label}</div>
      <div style={{ background: tint ? hexToRgba(tint, isDark ? 0.22 : 0.15) : (isDark ? '#0f1724' : '#fff'),
        border:`1px solid ${tint ? hexToRgba(tint, 0.55) : (isDark ? '#2a3550' : '#d8d8d0')}`,
        borderRadius:6, padding:'7px 10px', fontSize:13, minHeight:33, boxSizing:'border-box',
        color: empty ? (isDark ? '#5a6a8a' : '#aaa') : (isDark ? '#ccd0dc' : '#1a1a1a'),
        fontFamily: mono ? "'IBM Plex Mono', monospace" : 'inherit',
        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
        {value || '—'}
      </div>
    </div>
  );
}

// Small local sub-component so the Receipt Note and Internal Note fields
// share identical structure/spacing. When a "+ Add note" style action is
// added to the Receipt Note later, it drops into the same header row and
// alignment stays free.
// Save-on-blur variant of LsField. Deliberately no Save button — these are
// small independent facts, not a form, and the status dropdown next to them
// already behaves this way. Consequence: there is no Cancel either, so a
// change is committed the moment focus leaves. Fine for these fields;
// deliberately NOT how the receipt note works, since that's customer-facing.
function LsEditField({ label, value, isDark, mono = false, onSave, options = null, type = 'text', disabled = false }) {
  const [draft, setDraft] = React.useState(value ?? '');
  const [saving, setSaving] = React.useState(false);
  const [flash, setFlash] = React.useState(false);

  React.useEffect(() => { setDraft(value ?? ''); }, [value]);

  const commit = async (next) => {
    const v = next !== undefined ? next : draft;
    if (String(v) === String(value ?? '')) return;
    setSaving(true);
    const ok = await onSave(v);
    setSaving(false);
    if (ok) { setFlash(true); setTimeout(() => setFlash(false), 900); }
    else setDraft(value ?? '');
  };

  const box = {
    width:'100%', background: isDark ? '#0f1724' : '#fff',
    border:`1px solid ${flash ? '#2ecc71' : (isDark ? '#2a3550' : '#d8d8d0')}`,
    borderRadius:6, padding:'7px 10px', fontSize:13, minHeight:33, boxSizing:'border-box',
    color: isDark ? '#ccd0dc' : '#1a1a1a',
    fontFamily: mono ? "'IBM Plex Mono', monospace" : 'inherit',
    outline:'none', opacity: saving || disabled ? 0.6 : 1,
    transition:'border-color 0.3s',
  };

  return (
    <div>
      <div style={{ fontSize:10, color: isDark ? '#5a6a8a' : '#999', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4, fontWeight:600 }}>{label}</div>
      {options ? (
        <select value={draft} disabled={saving || disabled} style={{ ...box, cursor: disabled ? 'default' : 'pointer' }}
          onChange={e => { setDraft(e.target.value); commit(e.target.value); }}>
          {options.map(o => <option key={o.id} value={o.id} style={{ background: isDark ? '#0f1724' : '#fff', color: isDark ? '#ccd0dc' : '#1a1a1a' }}>{o.name}</option>)}
        </select>
      ) : (
        <input type={type} value={draft} disabled={saving || disabled} style={box}
          onChange={e => setDraft(e.target.value)}
          onBlur={() => commit()}
          onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') { setDraft(value ?? ''); e.currentTarget.blur(); } }} />
      )}
    </div>
  );
}

function NoteField({ label, action, isDark, T, children }) {
  return (
    <div>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', minHeight:24, marginBottom:4 }}>
        <div style={{ fontSize:10, color: isDark ? '#5a6a8a' : '#999', textTransform:'uppercase', letterSpacing:'0.08em', fontWeight:600 }}>{label}</div>
        {action /* right-slot: button, spinner, whatever — reserves the row height even when null */}
      </div>
      {children}
    </div>
  );
}

function GhostRow({ badge, bar1, bar2, isDark }) {
  const ghostBg = isDark ? '#1e2a44' : '#e8e8e0';
  const ghostBg2 = isDark ? '#18223a' : '#efefe8';
  return (
    <div style={{ border:`1px dashed ${isDark ? '#2a3550' : '#d0d0c8'}`, borderRadius:8, padding:'10px 12px',
      display:'flex', alignItems:'center', gap:10 }}>
      <span style={{ fontSize:10, fontWeight:700, padding:'3px 8px', borderRadius:4, whiteSpace:'nowrap',
        background: ghostBg, color: isDark ? '#5a6a8a' : '#999', border:`1px dashed ${isDark ? '#2a3550' : '#d0d0c8'}`,
        minWidth:70, textAlign:'center' }}>{badge}</span>
      <div style={{ flex:1 }}>
        <div style={{ height:8, width:bar1, background:ghostBg, borderRadius:4, marginBottom: bar2 ? 6 : 0 }} />
        {bar2 && <div style={{ height:6, width:bar2, background:ghostBg2, borderRadius:4 }} />}
      </div>
      <div style={{ height:8, width:48, background:ghostBg, borderRadius:4 }} />
    </div>
  );
}

// Shared Lightspeed-style ticket presentation: header card (fields grid +
// totals rail) and line-items card (real rows or ghost placeholders).
// Rendered by BOTH ManagerReviewWizard and TicketDetail — one implementation,
// same pattern as the SmsConversation extraction.
// What this ticket can use: what is in stock, less what OTHER tickets hold.
// Never more than is on the shelf.
export function availableToThisTicket(s, thisLineQty) {
  const othersHold = Math.max(0, (Number(s.reserved) || 0) - (Number(thisLineQty) || 0));
  return (Number(s.qoh) || 0) - othersHold;
}

export default function TicketCard({ theme, ticket = null, ticketDetail, loadingDetail = false, flags = null, onQuantityChange = null, onDeleteLineItem = null, onEditLineItem = null, addItemSlot = null, internalNoteEditor = null, payments = null, lineStock = null, statusEditor = null, receiptNoteEditor = null, fieldEditor = null, totalsPending = false }) {
  const T = getTheme(theme);
  const isDark = theme === 'dark';
  const checkedOut = !!(ticketDetail?.saleID && ticketDetail.saleID !== '0');

  // Prefer queue-row fields (richer risk context), fall back to the detail payload
  const displayId = ticket?.displayId || ticketDetail?.displayId || '';
  const status = ticket?.status || ticketDetail?.status || '';
  const customerName = ticket?.customer || ticketDetail?.customer?.name || '';
  const tech = ticket?.tech || ticketDetail?.tech || '';
  const bikeName = (ticketDetail?.bike?.description && ticketDetail.bike.description !== '—')
    ? ticketDetail.bike.description : (ticket?.bike || '');
  // Customer Item names the bike as one thing, so it carries the colour too —
  // "Cannondale Black/red" rather than a third identical "Cannondale". The
  // Description field below deliberately stays bare: Colour sits right beside
  // it as its own editable field, and repeating it there would be noise.
  const bikeItemLabel = ticketDetail?.bike?.label || bikeName;
  const timeIn = ticketDetail?.timeIn || ticket?.timeIn || null;
  const etaOut = ticket ? ticket.etaOut : (ticketDetail?.etaOut && ticketDetail.etaOut !== '0000-00-00 00:00:00' ? ticketDetail.etaOut : null);
  const daysOpen = ticket?.daysOpen ?? (timeIn ? Math.floor((new Date() - new Date(timeIn)) / (1000*60*60*24)) : null);
  const isOverdue = ticket?.isOverdue || false;
  const isDueSoon = ticket?.isDueSoon || false;
  const hasPromiseBroken = ticket?.hasPromiseBroken || false;

  const phoneDisplay = fmtPhone(ticketDetail?.customer?.phone);

  const lineItems = ticketDetail?.lineItems || [];
  // Labor/Parts are shown GROSS and the discount is broken out on its own row,
  // so the column reads the way the Lightspeed invoice copy does and actually
  // adds up. line.subtotal from the server is already net of the line discount,
  // so gross = subtotal + discountAmount. A warranty line is not charged at all,
  // so it contributes nothing to either row.
  const grossOf = (i) => i.warranty ? 0 : parseFloat(i.subtotal || 0) + parseFloat(i.discountAmount || 0);
  const laborTotal = lineItems.filter(i => !i.isInventory).reduce((sum, i) => sum + grossOf(i), 0);
  const partsTotal = lineItems.filter(i => i.isInventory).reduce((sum, i) => sum + grossOf(i), 0);
  const discountTotal = lineItems.reduce((sum, i) => sum + (i.warranty ? 0 : parseFloat(i.discountAmount || 0)), 0);

  const cardStyle = {
    background: isDark ? '#141d30' : '#fbfbf7',
    border:`1px solid ${isDark ? '#243050' : '#e0e0d8'}`,
    borderRadius:10,
  };

  return (
    <>
      {/* ═══ Lightspeed-style ticket card ═══ */}
      <div style={{ ...cardStyle, padding:'18px 20px' }}>

        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:16,
          borderBottom:`1px solid ${isDark ? '#243050' : '#e0e0d8'}`, paddingBottom:14, marginBottom:16 }}>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap', marginBottom:4 }}>
              <span style={{ fontFamily:"'IBM Plex Mono', monospace", fontSize:13, color: isDark ? '#5a8ab8' : '#888', fontWeight:600 }}>{displayId}</span>
              <span style={S.statusPill(status)}>{status}</span>
              {hasPromiseBroken && (
                <span style={{ fontSize:11, padding:'2px 8px', borderRadius:4,
                  background: isDark ? '#2a0f0f' : '#fde8e8', color:'#e74c3c', fontWeight:600 }}>
                  Broken Promise
                </span>
              )}
              {flags && flags.filter(f => !f.label.startsWith('Broken Promise')).map((f, i) => {
                const bc = f.level === 'danger'
                  ? { bg: isDark ? '#2a0f0f' : '#fde8e8', color:'#e74c3c' }
                  : f.level === 'revenue'
                    ? { bg: isDark ? '#1a2a0a' : '#fefbe2', color:'#c8a020' }
                    : { bg: isDark ? '#2a1f0a' : '#fef3e2', color:'#e67e22' };
                return <span key={i} style={{ fontSize:11, padding:'2px 8px', borderRadius:4, fontWeight:600, background:bc.bg, color:bc.color }}>{f.label}</span>;
              })}
            </div>
            <div style={{ fontSize:22, fontWeight:700, color:T.text }}>{customerName}</div>
            {phoneDisplay && (
              <div style={{ fontSize:13, color:T.textSub, marginTop:2 }}>Mobile {phoneDisplay}</div>
            )}
          </div>
          <div style={{ textAlign:'right', fontSize:13, lineHeight:1.9, whiteSpace:'nowrap' }}>
            <div style={{ color:T.textSub }}>Tech <span style={{ color:T.text, fontWeight:600 }}>{tech}</span></div>
            <div style={{ color:T.textSub }}>Days Open <span style={{ fontWeight:600,
              color: daysOpen > 5 ? '#e74c3c' : daysOpen >= 3 ? '#e67e22' : T.text }}>{daysOpen != null ? `${daysOpen}d` : '—'}</span></div>
            <div style={{ color:T.textSub }}>Promise Date <span style={{ fontWeight:600,
              color: isOverdue ? '#e74c3c' : isDueSoon ? '#e67e22' : T.text }}>{etaOut ? fmtDate(etaOut) : '—'}</span></div>
          </div>
        </div>

        <div style={{ display:'grid', gridTemplateColumns:'1fr 200px', gap:18 }}>
          <div style={{ display:'flex', flexDirection:'column', gap:10, minWidth:0 }}>
            <div style={{ display:'grid', gridTemplateColumns:'150px 1fr', gap:10 }}>
              {statusEditor ? (
                <div>
                  <div style={{ fontSize:10, color: isDark ? '#5a6a8a' : '#999', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4, fontWeight:600 }}>Status</div>
                  <select value={statusEditor.currentId || ''} disabled={statusEditor.saving}
                    onChange={e => statusEditor.onChange(e.target.value)}
                    style={{ width:'100%', background: ticketDetail?.statusColor ? hexToRgba(ticketDetail.statusColor, isDark ? 0.22 : 0.15) : (isDark ? '#0f1724' : '#fff'),
                      border:`1px solid ${ticketDetail?.statusColor ? hexToRgba(ticketDetail.statusColor, 0.55) : (isDark ? '#2a3550' : '#d8d8d0')}`,
                      borderRadius:6, padding:'7px 10px', fontSize:13, minHeight:33, boxSizing:'border-box',
                      color: isDark ? '#ccd0dc' : '#1a1a1a', cursor: statusEditor.saving ? 'wait' : 'pointer',
                      opacity: statusEditor.saving ? 0.6 : 1, fontFamily:'inherit', outline:'none' }}>
                    {statusEditor.options.map(s => (
                      <option key={s.id} value={s.id}
                        style={s.color
                          ? { background: s.color, color: readableOn(s.color) }
                          : { background: isDark ? '#0f1724' : '#fff', color: isDark ? '#ccd0dc' : '#1a1a1a' }}>{s.name}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <LsField label="Status" value={status} isDark={isDark} tint={ticketDetail?.statusColor} />
              )}
              {fieldEditor && !checkedOut && Array.isArray(fieldEditor.bikes) ? (
                // One of the customer's bikes, as Lightspeed's Customer Item
                // drop-down offers (7 Oct). "New bike" takes the bike off the
                // ticket; describing it below then creates the new one.
                <LsEditField label="Customer Item" isDark={isDark}
                  value={ticketDetail?.bike?.id ? String(ticketDetail.bike.id) : ''}
                  options={[
                    { id: '', name: ticketDetail?.bike?.id ? 'New bike (describe it below)' : 'New bike: describe it below, or pick one' },
                    ...(ticketDetail?.bike?.id && !fieldEditor.bikes.some(b => b.id === String(ticketDetail.bike.id))
                      ? [{ id: String(ticketDetail.bike.id), name: bikeItemLabel || `Bike #${ticketDetail.bike.id}` }] : []),
                    ...fieldEditor.bikes.map(b => ({ id: b.id, name: [b.label, b.size, b.serial && `SN ${b.serial}`].filter(Boolean).join(' · ') })),
                  ]}
                  onSave={v => fieldEditor.selectBike(v)} />
              ) : (
                <LsField label="Customer Item" value={bikeItemLabel} isDark={isDark} empty={!bikeItemLabel} />
              )}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1.2fr', gap:10 }}>
              {fieldEditor && !checkedOut ? (
                <>
                  {/* NOT disabled when there is no bike yet. These carried
                      `disabled={!ticketDetail?.bike?.id}` until 4 Sep, which was
                      honest about the old server -- saving with no serializedID
                      did nothing -- but meant a ticket that arrived without a
                      bike could never be given one. PUT /:id/bike now creates
                      and attaches the record when there is none, so the fields
                      are editable and the server asks for a description first
                      if the tech starts with the colour. */}
                  <LsEditField label="Description" value={ticketDetail?.bike?.description === '—' ? '' : ticketDetail?.bike?.description} isDark={isDark}
                    onSave={v => fieldEditor.saveBike({ description: v })} />
                  <LsEditField label="Color" value={ticketDetail?.bike?.color} isDark={isDark}
                    onSave={v => fieldEditor.saveBike({ color: v })} />
                  <LsEditField label="Size" value={ticketDetail?.bike?.size} isDark={isDark}
                    onSave={v => fieldEditor.saveBike({ size: v })} />
                  <LsEditField label="Serial" value={ticketDetail?.bike?.serial} isDark={isDark} mono
                    onSave={v => fieldEditor.saveBike({ serial: v })} />
                </>
              ) : (
                <>
                  <LsField label="Description" value={bikeName} isDark={isDark} empty={!bikeName} />
                  <LsField label="Color" value={ticketDetail?.bike?.color} isDark={isDark} empty={!ticketDetail?.bike?.color} />
                  <LsField label="Size" value={ticketDetail?.bike?.size} isDark={isDark} empty={!ticketDetail?.bike?.size} />
                  <LsField label="Serial" value={ticketDetail?.bike?.serial} isDark={isDark} mono empty={!ticketDetail?.bike?.serial} />
                </>
              )}
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:10 }}>
              {fieldEditor && !checkedOut ? (
                <LsEditField label="Employee" value={fieldEditor.employeeId || ''} isDark={isDark}
                  options={fieldEditor.employees} onSave={v => fieldEditor.saveFields({ employeeID: v })} />
              ) : (
                <LsField label="Employee" value={tech} isDark={isDark} />
              )}
              <LsField label="Date In" value={timeIn ? fmtDateTime(timeIn) : '—'} isDark={isDark} empty={!timeIn} />
              {fieldEditor && !checkedOut ? (
                <LsEditField label="Due" type="date" isDark={isDark}
                  value={etaOut ? new Date(etaOut).toISOString().slice(0, 10) : ''}
                  onSave={v => fieldEditor.saveFields({ etaOut: v ? `${v}T12:00:00+00:00` : '' })} />
              ) : (
                <LsField label="Due" value={etaOut ? fmtDateTime(etaOut) : '—'} isDark={isDark} empty={!etaOut} />
              )}
            </div>

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginTop:2 }}>
              <NoteField label="Receipt Note" isDark={isDark} T={T}
                action={
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    {ticketDetail?.receiptNoteBy && !receiptNoteEditor?.editing && (
                      <span style={{ fontSize:10.5, fontStyle:'italic', color: isDark ? '#5a6a8a' : '#aaa', fontWeight:400 }}>
                        — {ticketDetail.receiptNoteBy}
                        {ticketDetail.receiptNoteAt ? `, ${new Date(ticketDetail.receiptNoteAt).toLocaleString('en-US', { month:'numeric', day:'numeric', year:'numeric', hour:'numeric', minute:'2-digit' })}` : ''}
                      </span>
                    )}
                    {receiptNoteEditor && !checkedOut && (
                      receiptNoteEditor.editing ? (
                        <div style={{ display:'flex', gap:6 }}>
                          <button onClick={receiptNoteEditor.onCancel} disabled={receiptNoteEditor.saving}
                            style={{ fontSize:10, padding:'2px 8px', borderRadius:4, cursor:'pointer',
                              background:'transparent', border:`1px solid ${isDark ? '#2e3d58' : '#d0d0c8'}`,
                              color:T.textSub, fontFamily:'Arial, sans-serif' }}>
                            Cancel
                          </button>
                          <button onClick={receiptNoteEditor.onSave} disabled={receiptNoteEditor.saving}
                            style={{ fontSize:10, padding:'2px 8px', borderRadius:4, cursor:'pointer',
                              background:'#3498db', border:'1px solid #3498db', color:'#fff',
                              fontFamily:'Arial, sans-serif', opacity: receiptNoteEditor.saving ? 0.6 : 1 }}>
                            {receiptNoteEditor.saving ? 'Saving…' : 'Save'}
                          </button>
                        </div>
                      ) : (
                        <button onClick={receiptNoteEditor.onOpen}
                          style={{ fontSize:10, padding:'2px 8px', borderRadius:4, cursor:'pointer',
                            background:'transparent', border:`1px solid ${isDark ? '#2e3d58' : '#d0d0c8'}`,
                            color:T.textSub, fontFamily:'Arial, sans-serif' }}>
                          Edit note
                        </button>
                      )
                    )}
                  </div>
                }>
                {receiptNoteEditor?.editing ? (
                  <textarea value={receiptNoteEditor.value}
                    onChange={e => receiptNoteEditor.setValue(e.target.value)}
                    autoFocus
                    placeholder="This text prints on the customer's receipt."
                    style={{ background: isDark ? '#0f1724' : '#fff', border:`1px solid #3498db`, borderRadius:6, padding:'10px 12px',
                      fontSize:13, color: T.text, lineHeight:1.7, height:120, width:'100%',
                      resize:'vertical', boxSizing:'border-box', fontFamily:'inherit', outline:'none' }} />
                ) : (
                  <div style={{ background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`, borderRadius:6, padding:'10px 12px',
                    fontSize:13, color: ticketDetail?.note ? T.textSub : (isDark ? '#5a6a8a' : '#aaa'),
                    lineHeight:1.7, whiteSpace:'pre-wrap', height:120,
                    fontStyle: ticketDetail?.note ? 'normal' : 'italic',
                    overflowY:'auto', boxSizing:'border-box' }}>
                    {loadingDetail ? 'Loading…' : (ticketDetail?.note || 'No receipt note — checklist may not have been submitted yet')}
                  </div>
                )}
              </NoteField>

              <NoteField
                label="Internal Note"
                isDark={isDark}
                T={T}
                action={internalNoteEditor && !internalNoteEditor.editing ? (
                  <button onClick={internalNoteEditor.onOpen}
                    style={{ fontSize:10, padding:'2px 8px', borderRadius:4, cursor:'pointer',
                      background:'transparent', border:`1px solid ${isDark ? '#2e3d58' : '#d0d0c8'}`,
                      color:T.textSub, fontFamily:'Arial, sans-serif' }}>
                    + Add note
                  </button>
                ) : null}
              >
                {internalNoteEditor?.editing ? (
                  <div>
                    <textarea
                      autoFocus
                      value={internalNoteEditor.noteInput}
                      onChange={e => internalNoteEditor.setNoteInput(e.target.value)}
                      placeholder="Enter note — timestamped and prepended…"
                      rows={4}
                      style={{ width:'100%', background: isDark ? '#0f1724' : '#fff',
                        border:`1px solid ${internalNoteEditor.noteInput.trim() ? '#3498db' : (isDark ? '#2a3550' : '#d8d8d0')}`,
                        borderRadius:6, padding:'8px 10px', color:T.text, fontSize:13,
                        fontFamily:'Arial, sans-serif', resize:'none', outline:'none',
                        boxSizing:'border-box', marginBottom:6 }}
                    />
                    <div style={{ display:'flex', gap:6, alignItems:'center' }}>
                      <button onClick={internalNoteEditor.onSave} disabled={!internalNoteEditor.noteInput.trim() || internalNoteEditor.savingNote}
                        style={{ fontSize:12, padding:'5px 12px', borderRadius:5, cursor: internalNoteEditor.noteInput.trim() ? 'pointer' : 'not-allowed',
                          background: internalNoteEditor.noteInput.trim() ? '#3498db' : (isDark ? '#243050' : '#d0d0c8'),
                          color: internalNoteEditor.noteInput.trim() ? '#fff' : T.textSub,
                          border:'none', fontFamily:'Arial, sans-serif', opacity: internalNoteEditor.savingNote ? 0.7 : 1 }}>
                        {internalNoteEditor.savingNote ? 'Saving…' : '✓ Save'}
                      </button>
                      <button onClick={internalNoteEditor.onCancel}
                        style={{ fontSize:12, padding:'5px 10px', borderRadius:5, cursor:'pointer',
                          background:'transparent', border:`1px solid ${isDark ? '#2e3d58' : '#d0d0c8'}`,
                          color:T.textSub, fontFamily:'Arial, sans-serif' }}>
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div style={{ background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`, borderRadius:6, padding:'10px 12px',
                    fontSize:13, color: ticketDetail?.internalNote ? T.text : T.textSub,
                    lineHeight:1.7, whiteSpace:'pre-wrap', height:120,
                    fontStyle: ticketDetail?.internalNote ? 'normal' : 'italic',
                    overflowY:'auto', boxSizing:'border-box' }}>
                    {loadingDetail ? 'Loading…' : (ticketDetail?.internalNote || 'No internal note')}
                  </div>
                )}
              </NoteField>
            </div>
          </div>

          <div style={{ background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`,
            borderRadius:8, padding:'12px 14px', alignSelf:'start' }}>
            {/* Labor and Parts are summed from lineItems right here, so after an
                optimistic append they are correct immediately. Tax and Total come
                from the server — deriving them client-side would mean a second
                copy of the money math, which is what undercharged a customer in
                July — so while a reconcile is in flight they fade rather than
                assert a figure that has not caught up with the list above. */}
            {[
              ['Labor', laborTotal, false, false],
              ['Parts', partsTotal, false, false],
              ...(discountTotal > 0.004 ? [['Discount', discountTotal, false, true]] : []),
              ['Fees', 0, false, false],
              ['Tax', parseFloat(ticketDetail?.taxAmount || 0), true, false],
            ].map(([label, val, serverDerived, negative], i, arr) => (
              <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'4px 0', fontSize:13,
                borderBottom: i === arr.length - 1 ? `1px solid ${isDark ? '#243050' : '#e0e0d8'}` : 'none' }}>
                <span style={{ color:T.textSub }}>{label}</span>
                <span style={{ color: negative ? '#2ecc71' : T.text, opacity: serverDerived && totalsPending ? 0.35 : 1,
                  transition:'opacity 140ms ease' }}>{negative ? '-' : ''}${val.toFixed(2)}</span>
              </div>
            ))}
            <div style={{ display:'flex', justifyContent:'space-between', padding:'8px 0 2px', fontSize:15, fontWeight:700, color:T.text }}>
              <span>Total</span>
              <span style={{ opacity: totalsPending ? 0.35 : 1, transition:'opacity 140ms ease' }}>
                ${parseFloat(ticketDetail?.totalValue || 0).toFixed(2)}
              </span>
            </div>
            {/* Real payment-request history. Replaces a hardcoded "Stripe
                Invoices / No deposits" stub that rendered unconditionally —
                a static claim sitting next to a real total. Renders only when
                a consumer actually passes data, so an unwired consumer shows
                nothing rather than reverting to a false statement. */}
            {Array.isArray(payments) && (
              <div style={{ borderTop:`1px solid ${isDark ? '#243050' : '#e0e0d8'}`, marginTop:8, paddingTop:8, fontSize:12 }}>
                <div style={{ color:T.textSub, fontWeight:600, marginBottom:4 }}>Payments</div>
                {payments.length === 0 ? (
                  <div style={{ color: isDark ? '#5a6a8a' : '#aaa' }}>None requested</div>
                ) : (() => {
                  const now = new Date();
                  const paidTotal = payments
                    .filter(p => p.status === 'paid')
                    .reduce((s, p) => s + Number(p.amount || 0), 0);
                  const meta = (p) => {
                    if (p.status === 'paid')   return { label:'Prepaid',  color:'#2ecc71' };
                    if (p.status === 'failed') return { label:'Declined', color:'#e74c3c' };
                    if (p.expiresAt && new Date(p.expiresAt) < now)
                                               return { label:'Expired',  color: isDark ? '#7a8ba8' : '#8a8a82' };
                    return { label:'Sent', color:'#e67e22' };
                  };
                  return (
                    <>
                      {payments.map(p => {
                        const m = meta(p);
                        const when = p.paidAt || p.createdAt;
                        return (
                          <div key={p.id} style={{ display:'flex', justifyContent:'space-between',
                            alignItems:'baseline', gap:8, padding:'3px 0' }}>
                            <span style={{ color:m.color, fontWeight:600, whiteSpace:'nowrap' }}>{m.label}</span>
                            <span style={{ color:T.textSub, fontSize:11, flex:1, textAlign:'right' }}>
                              {when ? new Date(when).toLocaleDateString('en-US', { month:'short', day:'numeric' }) : ''}
                            </span>
                            <span style={{ color:T.text, whiteSpace:'nowrap' }}>
                              ${Number(p.amount || 0).toFixed(2)}
                            </span>
                          </div>
                        );
                      })}
                      {payments.some(p => p.depositError) && (
                        <div style={{ marginTop:6, padding:'6px 8px', borderRadius:6,
                          background: isDark ? '#2a0f0f' : '#fde8e8', color:'#e74c3c',
                          fontSize:11, lineHeight:1.5, fontWeight:600 }}>
                          ⚠ A payment was collected in Stripe but not applied to this customer's
                          Lightspeed account. Apply it manually before checkout.
                        </div>
                      )}
                      {paidTotal > 0 && (
                        <div style={{ display:'flex', justifyContent:'space-between',
                          borderTop:`1px solid ${isDark ? '#243050' : '#e0e0d8'}`, marginTop:5, paddingTop:5,
                          fontWeight:700, color:T.text }}>
                          <span>Prepaid</span>
                          <span>${paidTotal.toFixed(2)}</span>
                        </div>
                      )}
                      {paidTotal > 0 && paidTotal + 0.01 < parseFloat(ticketDetail?.totalValue || 0) && (
                        <div style={{ color:T.textSub, fontSize:11, marginTop:3 }}>
                          ${(parseFloat(ticketDetail?.totalValue || 0) - paidTotal).toFixed(2)} due at pickup
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        </div>
      </div>

      {addItemSlot}

      {/* ═══ Line items — real or placeholder ═══ */}
      <LineItemsList theme={theme} lineItems={lineItems} loadingDetail={loadingDetail} checkedOut={checkedOut}
        onQuantityChange={onQuantityChange} onDeleteLineItem={onDeleteLineItem} onEditLineItem={onEditLineItem}
        lineStock={lineStock} />
    </>
  );
}
// The ticket's line items, as Ticket Detail shows them. A component of its own
// since 2 Oct so the bench screen renders the same list with the same edit,
// quantity and remove controls. `large` scales type and touch targets up a little for a
// tablet at the stand and lets the list grow instead of scrolling inside a
// 320px box. Lines whose id is in lockedLineIds show but cannot be edited,
// re-counted or removed: the bench uses it for the tech timer's own line,
// which the timer rewrites and must find again by id.
export function LineItemsList({ theme, lineItems = [], loadingDetail = false, checkedOut = false, onQuantityChange = null,
  onDeleteLineItem = null, onEditLineItem = null, lineStock = null, lockedLineIds = null, lockedNote = '', large = false, title = '' }) {
  const T = getTheme(theme);
  const isDark = theme === 'dark';
  const fs = (n) => large ? Math.round(n * 1.12) : n;
  const locked = new Set((lockedLineIds || []).map(String));
  // The timer's own lines are locked on every screen, not only on the bench
  // (22 Oct): the server marks them, so the ticket view no longer offers a
  // $0 time record as editable labor.
  const isLocked = (item) => locked.has(String(item.id)) || !!item.isTimerLine;
  const noteFor = (item) => (item.isTimerLine ? 'Recorded by My Workbench. Change it from the timer.' : lockedNote);
  const cardStyle = {
    background: isDark ? '#141d30' : '#fbfbf7',
    border:`1px solid ${isDark ? '#243050' : '#e0e0d8'}`,
    borderRadius:10,
  };
  return (
          <div style={{ ...cardStyle, padding:'16px 20px' }}>
      <div style={{ fontSize:fs(10), color: isDark ? '#5a6a8a' : '#999', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:10, fontWeight:600 }}>
        {title || `Line Items (${lineItems.length})`}
      </div>
      {loadingDetail ? (
        <div style={{ fontSize:fs(13), color:T.textSub, fontStyle:'italic' }}>Loading details…</div>
      ) : lineItems.length > 0 ? (
        <>
          <div style={{ display:'flex', flexDirection:'column', gap:8, maxHeight: large ? 'none' : 320, overflowY: large ? 'visible' : 'auto', padding:'2px' }}>
            {lineItems.map((item, i) => (
              <div key={i} style={{ display:'flex', alignItems:'center', gap:10, padding: large ? '10px 12px' : '10px 12px', borderRadius:8,
                background: isDark ? '#1e2a40' : '#f4f4ef' }}>
                {(() => {
                  // Three-badge scheme keyed off backend lineType.
                  // Fallback maintained for older payloads that only had isInventory.
                  const kind = item.lineType || (item.isInventory ? 'parts' : 'labor');
                  const badgeStyles = {
                    parts: { bg: isDark ? '#0f2a15' : '#d4edda', color: '#2ecc71', label: 'Parts' },
                    labor: { bg: isDark ? '#0f1e30' : '#e8f0f8', color: '#3498db', label: 'Labor' },
                    misc:  { bg: isDark ? '#2a1f0a' : '#faeeda', color: '#e67e22', label: 'Misc' },
                  };
                  const b = badgeStyles[kind];
                  return (
                    <span style={{ fontSize:fs(10), fontWeight:700, padding:'3px 8px', borderRadius:4, whiteSpace:'nowrap',
                      background:b.bg, color:b.color }}>
                      {b.label}
                    </span>
                  );
                })()}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:7, flexWrap:'wrap' }}>
      {(() => {
        // The description IS the edit affordance now — a dedicated pencil put
        // the control at the far right, nowhere near the text it edits. Dashed
        // underline is the same click-to-edit cue the queue uses for tech and
        // customer. A checked-out ticket renders as plain text with no cue, so
        // the affordance never lies.
        //
        // Parts became editable on Aug 18. They were excluded because the only
        // thing writable on a WorkorderItem was quantity, which has its own
        // inline control — but discount, warranty, tax and note are writable
        // too, and until then could only be set in Lightspeed.
        const editable = !checkedOut && onEditLineItem && !isLocked(item);
        const label = item.description || `Item ${i+1}`;
        if (!editable) return <span style={{ fontSize:fs(13), fontWeight:600, color:T.text }}>{label}</span>;
        return (
          <span
            onClick={() => onEditLineItem(item)}
            title="Click to edit this line"
            onMouseEnter={e => { e.currentTarget.style.color = '#3498db'; e.currentTarget.style.borderBottomColor = '#3498db'; }}
            onMouseLeave={e => { e.currentTarget.style.color = T.text; e.currentTarget.style.borderBottomColor = isDark ? '#3a4a6a' : '#b0b0a8'; }}
            style={{ fontSize:fs(13), fontWeight:600, color:T.text, cursor:'pointer',
              borderBottom:`1px dashed ${isDark ? '#3a4a6a' : '#b0b0a8'}`, paddingBottom:1,
              transition:'color 0.12s, border-color 0.12s' }}>
            {label}
          </span>
        );
      })()}
      {item.isTimerLine && (
        // Staff-facing only: the receipt shows the line's own wording.
        <span style={{ fontSize:fs(9.5), fontWeight:700, padding:'2px 6px', borderRadius:4, whiteSpace:'nowrap',
          background: isDark ? '#243050' : '#e8e8e0', color:T.textSub }}>TECH TIME · $0</span>
      )}
      {item.warranty && (
        <span style={{ fontSize:fs(9.5), fontWeight:700, padding:'2px 6px', borderRadius:4, whiteSpace:'nowrap',
          background: isDark ? '#2a1f0a' : '#faeeda', color:'#e67e22' }}>WARRANTY</span>
      )}
      {item.discountName && (
        <span style={{ fontSize:fs(9.5), fontWeight:700, padding:'2px 6px', borderRadius:4, whiteSpace:'nowrap',
          background: isDark ? '#0f2a15' : '#d4edda', color:'#2ecc71' }}>
          {item.discountName}{item.discountAmount ? ` −$${Number(item.discountAmount).toFixed(2)}` : ''}
        </span>
      )}
      {item.taxable === false && (
        <span style={{ fontSize:fs(9.5), fontWeight:700, padding:'2px 6px', borderRadius:4, whiteSpace:'nowrap',
          background: isDark ? '#243050' : '#e8e8e0', color:T.textSub }}>NO TAX</span>
      )}
      {/* Quote lines only (23 Oct): a part that has to be ordered, and from whom. */}
      {item.needsOrdering && (
        <span title={[item.vendor, item.vendorPart && `part ${item.vendorPart}`].filter(Boolean).join(' · ') || 'Not in stock'}
          style={{ fontSize:fs(9.5), fontWeight:700, padding:'2px 6px', borderRadius:4, whiteSpace:'nowrap',
          background: isDark ? '#2a1f0a' : '#fdf3e2', color:'#e67e22' }}>
          TO ORDER{item.vendor ? ` · ${item.vendor}` : ''}{item.vendorPart ? ` ${item.vendorPart}` : ''}
        </span>
      )}
    </div>
    {(() => {
      // Who did the work, on the line rather than buried in the edit modal.
      // Parts have no tech. Unassigned is called out rather than hidden —
      // labor with nobody on it is the case worth noticing.
      const kind = item.lineType || (item.isInventory ? 'parts' : 'labor');
      if (kind === 'parts') return null;
      // A quote has no tech yet: the work is assigned once it is a ticket.
      if (item.quoteLine) return null;
      const unassigned = !item.tech || item.tech === 'Unassigned';
      // Rendered as a chip rather than a bare line: as plain muted text under
      // a bold title it read as part of the description and got lost.
      const tint = unassigned ? '#e67e22' : '#3498db';
      return (
        <div style={{ marginTop:4 }}>
          <span style={{ display:'inline-flex', alignItems:'center', gap:4,
            fontSize:fs(10.5), fontWeight:700, letterSpacing:'0.02em',
            padding:'2px 8px', borderRadius:10, whiteSpace:'nowrap',
            background: unassigned
              ? (isDark ? '#2a1f0a' : '#faeeda')
              : (isDark ? '#0f1e30' : '#e8f0f8'),
            color: tint, border:`1px solid ${tint}55` }}>
            <i className="ti ti-tool" aria-hidden="true" style={{ fontSize:fs(11) }} />
            {unassigned ? 'Unassigned' : item.tech}
          </span>
        </div>
      );
    })()}
    {(() => {
      // Lightspeed counts this ticket's own units as reserved, so they are
      // taken back out of the reservations -- otherwise a part you're holding
      // reads "0 available," which is true globally but wrong here. Only what
      // Lightspeed actually reserved is taken back: a part just created from a
      // vendor catalog has nothing in stock and nothing reserved yet, and the
      // old "available + this line" sum showed it as 1 available (22 Oct).
      const s = item.isInventory && item.itemID ? (lineStock || {})[String(item.itemID)] : null;
      if (!s) return null;
      const free = availableToThisTicket(s, item.quantity);
      
      return (
        <div style={{ fontSize:fs(11), marginTop:2, color: T.textSub }}>
          {free} available
          {s.reserved > 0 && ` (${s.qoh} in stock, ${s.reserved} reserved)`}
        </div>
      );
    })()}
                  {item.note && (
                    <div style={{ fontSize:fs(12), color:T.textSub, marginTop:2, lineHeight:1.5, whiteSpace:'pre-wrap' }}>{item.note}</div>
                  )}
                  {isLocked(item) && noteFor(item) && (
                    <div style={{ fontSize:fs(11), color:T.textSub, marginTop:3, fontStyle:'italic' }}>🔒 {noteFor(item)}</div>
                  )}
                </div>
                {item.isInventory && !checkedOut && !isLocked(item) ? (
                  <input
                    type="number"
                    min="1"
                    step="1"
                    defaultValue={item.quantity}
                    onBlur={e => {
                      const val = parseInt(e.target.value, 10);
        if (!(val > 0) || val === item.quantity || !onQuantityChange) { e.target.value = item.quantity; return; }
        const s = item.itemID ? (lineStock || {})[String(item.itemID)] : null;
        if (s) {
          const free = availableToThisTicket(s, item.quantity);
          if (val > free) {
            const detail = s.reserved > 0
              ? `${s.qoh} in stock, ${s.reserved} reserved for other tickets — ${free} available to this one.`
              : `Only ${free} available.`;
            if (!window.confirm(`${item.description}\n\n${detail}\n\nSet quantity to ${val} anyway?`)) {
              e.target.value = item.quantity;
              return;
            }
          }
        }
        onQuantityChange(item, val);
                    }}
                    style={{ width: large ? 56 : 44, height: large ? 34 : 26, fontSize:fs(12), textAlign:'center',
                      background: isDark ? '#0f1724' : '#fff', border:`1px solid ${isDark ? '#2a3550' : '#d8d8d0'}`,
                      borderRadius:5, color:T.text, boxSizing:'border-box' }}
                  />
                ) : (
                  item.quantity > 1 && <span style={{ fontSize:fs(11), color:T.textSub }}>×{item.quantity}</span>
                )}
                <div style={{ fontSize:fs(13), fontWeight:600, color:T.text, whiteSpace:'nowrap', width:56, textAlign:'right' }}>
                  ${parseFloat(item.subtotal || 0).toFixed(2)}
                </div>
                {!checkedOut && onDeleteLineItem && !isLocked(item) && (
                  <button
                    onClick={() => onDeleteLineItem(item)}
                    title="Remove this item"
                    aria-label={`Remove ${item.description || 'this item'}`}
                    style={{ background:'transparent', border:'none', cursor:'pointer', color: isDark ? '#5a6a8a' : '#aaa',
                      fontSize:fs(15), lineHeight:1, padding: large ? 0 : '2px 4px', flexShrink:0,
                      width: large ? 36 : 'auto', height: large ? 36 : 'auto' }}>
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
          
        </>
      ) : (
        <>
          <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:10 }}>
            <GhostRow badge="Labor & Misc" bar1="45%" bar2="70%" isDark={isDark} />
            <GhostRow badge="Parts" bar1="60%" isDark={isDark} />
            <GhostRow badge="Parts" bar1="38%" isDark={isDark} />
          </div>
          <div style={{ textAlign:'center', fontSize:fs(12), color: isDark ? '#5a6a8a' : '#999', fontStyle:'italic' }}>
            No parts or labor on this ticket yet — items added in Lightspeed will appear here
          </div>
        </>
      )}
    </div>
  );
}
